// Mock des réponses IA pour le développement
// À remplacer par les vrais appels API une fois le backend implémenté

import authFetch from './authFetch';

export const mockAIResponse = async (message, cvData, sector) => {
    // Simuler un délai de réponse
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

    // Réponses contextuelles basées sur le message
    const responses = {
        'structure': {
            response: `Voici comment structurer votre CV de manière optimale :

1. **Informations personnelles** (en haut)
2. **Résumé professionnel** (2-3 phrases impactantes)
3. **Expériences professionnelles** (du plus récent au plus ancien)
4. **Formation** (diplômes et certifications)
5. **Compétences** (techniques et soft skills)
6. **Langues** (avec niveau)
7. **Projets/Centres d'intérêt** (optionnel)

Pour votre secteur (${sector}), je recommande de mettre l'accent sur les compétences techniques et les réalisations quantifiées.`,
            suggestions: [
                'Comment rédiger un résumé percutant ?',
                'Quels mots-clés utiliser pour mon secteur ?',
                'Comment présenter mes expériences ?'
            ]
        },
        'mots-clés': {
            response: `Voici les mots-clés essentiels pour le secteur ${sector} :

**Compétences techniques :**
${getSectorKeywords(sector)}

**Verbes d'action puissants :**
- Développé, Implémenté, Optimisé
- Géré, Coordonné, Dirigé
- Analysé, Résolu, Amélioré
- Créé, Conçu, Mis en place

**Conseils d'utilisation :**
- Intégrez-les naturellement dans vos descriptions
- Évitez la répétition excessive
- Adaptez-les à vos vraies expériences
- Utilisez les termes exacts des offres d'emploi`,
            suggestions: [
                'Comment intégrer ces mots-clés ?',
                'Quels mots-clés pour mon expérience ?',
                'Comment éviter la répétition ?'
            ]
        },
        'expériences': {
            response: `Pour présenter vos expériences de manière impactante :

**Structure recommandée :**
- Poste + Entreprise + Période
- Description courte du contexte
- Réalisations avec chiffres (si possible)
- Compétences utilisées

**Exemple pour ${sector} :**
"Développeur Full Stack - TechCorp (2020-2023)
Développement d'applications web pour 50 000+ utilisateurs
• Réduit le temps de chargement de 40% avec React
• Implémenté 15 nouvelles fonctionnalités
• Technologies : JavaScript, React, Node.js, MongoDB"

**Conseils :**
- Utilisez des verbes d'action
- Quantifiez vos réalisations
- Mentionnez les technologies
- Adaptez au poste visé`,
            suggestions: [
                'Comment quantifier mes réalisations ?',
                'Quels verbes utiliser ?',
                'Comment adapter à différents postes ?'
            ]
        },
        'compétences': {
            response: `Voici comment présenter vos compétences efficacement :

**Organisation recommandée :**
1. **Compétences techniques** (spécifiques au secteur)
2. **Soft skills** (communication, leadership, etc.)
3. **Outils et technologies**
4. **Langues** (avec niveau)

**Pour le secteur ${sector} :**
${getSectorSkills(sector)}

**Conseils de présentation :**
- Groupez par catégorie
- Indiquez le niveau (débutant, intermédiaire, expert)
- Mettez en avant les plus pertinentes pour le poste
- Ajoutez des certifications si vous en avez

**Évitez :**
- Les listes trop longues
- Les compétences trop génériques
- Les niveaux non justifiés`,
            suggestions: [
                'Comment évaluer mon niveau ?',
                'Quelles compétences mettre en avant ?',
                'Comment présenter mes certifications ?'
            ]
        }
    };

    // Détecter le type de question
    let responseType = 'general';
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('structur') || lowerMessage.includes('organis')) {
        responseType = 'structure';
    } else if (lowerMessage.includes('mots-clés') || lowerMessage.includes('keywords')) {
        responseType = 'mots-clés';
    } else if (lowerMessage.includes('expérienc') || lowerMessage.includes('travail')) {
        responseType = 'expériences';
    } else if (lowerMessage.includes('compétenc') || lowerMessage.includes('skills')) {
        responseType = 'compétences';
    }

    // Réponse par défaut si aucune correspondance
    if (!responses[responseType]) {
        return {
            response: `Merci pour votre question ! Je suis là pour vous aider à améliorer votre CV. 

Pour vous donner des conseils plus précis, pourriez-vous me dire :
- Sur quelle partie de votre CV vous souhaitez travailler ?
- Quel type de poste vous visez ?
- Quelles sont vos principales préoccupations ?

Je peux vous aider avec la structure, les mots-clés, la présentation des expériences, les compétences, et bien plus encore !`,
            suggestions: [
                'Comment structurer mon CV ?',
                'Quels mots-clés utiliser ?',
                'Comment présenter mes expériences ?',
                'Comment mettre en valeur mes compétences ?'
            ]
        };
    }

    return responses[responseType];
};

const getSectorKeywords = (sector) => {
    const keywords = {
        'informatique': `• Langages : JavaScript, Python, Java, C++, SQL
• Frameworks : React, Angular, Vue.js, Node.js, Django
• Outils : Git, Docker, AWS, Azure, Jenkins
• Méthodologies : Agile, Scrum, DevOps, CI/CD`,
        
        'marketing': `• Outils : Google Analytics, Facebook Ads, Mailchimp
• Plateformes : Google Ads, LinkedIn Ads, Instagram
• Concepts : SEO, SEM, Content Marketing, Growth Hacking
• Métriques : ROI, CTR, Conversion Rate, CAC`,
        
        'finance': `• Outils : Excel, Bloomberg, Reuters, SAP
• Concepts : Analyse financière, Gestion de portefeuille
• Normes : IFRS, GAAP, Solvabilité II
• Certifications : CFA, FRM, CAIA`,
        
        'ressources_humaines': `• Outils : ATS, SIRH, LinkedIn Recruiter
• Processus : Recrutement, Formation, Évaluation
• Concepts : GPEC, RPS, QVT, Marque employeur
• Compétences : Communication, Négociation, Écoute`
    };

    return keywords[sector] || `• Compétences spécifiques au secteur ${sector}
• Outils et technologies utilisés
• Méthodologies et processus
• Certifications pertinentes`;
};

const getSectorSkills = (sector) => {
    const skills = {
        'informatique': `**Développement :** JavaScript, Python, Java, React, Node.js
**Base de données :** SQL, MongoDB, PostgreSQL
**DevOps :** Git, Docker, AWS, CI/CD
**Soft skills :** Résolution de problèmes, Travail d'équipe, Apprentissage continu`,
        
        'marketing': `**Digital :** Google Analytics, Facebook Ads, SEO, Email Marketing
**Création :** Photoshop, Canva, Copywriting
**Analyse :** Data Analysis, A/B Testing, ROI Optimization
**Soft skills :** Créativité, Communication, Stratégie`,
        
        'finance': `**Analyse :** Excel, Bloomberg, Analyse financière, Risk Management
**Comptabilité :** IFRS, GAAP, Consolidation
**Trading :** Marchés financiers, Gestion de portefeuille
**Soft skills :** Précision, Analyse critique, Éthique`
    };

    return skills[sector] || `**Compétences techniques spécifiques au secteur
**Outils et technologies
**Soft skills pertinentes
**Certifications et formations`;
};

// Fonction pour simuler l'appel API
export const callAIApi = async (message, cvData, sector, conversationHistory, token, userId) => {
    try {
        // En développement, utiliser le mock (commenté pour passer à l'API)
        // if (process.env.NODE_ENV === 'development') {
        //     const mockResponse = await mockAIResponse(message, cvData, sector);
        //     // Simuler la réponse avec les crédits restants (1 crédit pour l'assistant IA)
        //     return {
        //         ...mockResponse,
        //         remainingCredits: Math.max(0, (localStorage.getItem('userCredits') || 10) - 1)
        //     };
        // }

        // Appeler la vraie API
        const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/ai/chat', {
            method: 'POST',
            headers: { 'X-User-Id': userId },
            body: JSON.stringify({
                message,
                cvData,
                sector,
                conversationHistory
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            if (response.status === 402 && errorData.insufficientCredits) {
                throw new Error('INSUFFICIENT_CREDITS');
            }
            throw new Error('Erreur API');
        }

        return await response.json();
    } catch (error) {
        console.error('Erreur AI API:', error);
        if (error.message === 'INSUFFICIENT_CREDITS') {
            throw error;
        }
                 // Fallback vers le mock en cas d'erreur
         const mockResponse = await mockAIResponse(message, cvData, sector);
         return {
             ...mockResponse,
             remainingCredits: Math.max(0, (localStorage.getItem('userCredits') || 10) - 1)
         };
    }
};
